/* eslint-disable */
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IPromsDomainTotalGraphData,
  IPromsLineGraphData,
  IPromsRange,
  IPromsTableData,
  ISurveyFormDisplayRuleYAxisLabel,
  ISurveyFormQuestionTableData,
  ISurveyFormScore,
  ISurveyPromsTableData,
} from "../../../../../../@types/Proms";
import { ReactComponent as PromsEmptyState } from "../../../../../../assets/images/proms-empty-state.svg";
import { SkeletonLoading } from "../../../../../../components/Skeleton";
import { createColumn, Table } from "../../../../../../components/Table";
import { getPatientSurveyFormsData } from "../../../../../../hooks/queries/proms";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { userService } from "../../../../../../services/userService";
import {
  getLongFormattedDate,
  getShortFormattedDate,
  LONG_DATE_TIME_FORMAT,
} from "../../../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { roundToX } from "../../../../../../utils/unitOperations";
import {
  DATE_FILTER_ALL,
  DATE_FILTER_CUSTOM,
  DateFilterOptionType,
} from "../../../MeasurementModal/components/CustomOptionsBar";
import {
  ImageContainer,
  NoContentMessage,
} from "../../PatientPromsTable.style";
import { PromsDomainTotalGraph } from "../PromsDomainTotalGraph";
import { PromsLineGraph } from "../PromsLineGraph";
import { PromsOptionsBar } from "../PromsOptionsBar";
import { PromsScatterGraph } from "../PromsScatterGraph";
import {
  PromDetailsModalContainer,
  Subtitle,
  ContentContainer,
  Title,
  FlexContainer,
  HeaderContainer,
  TitleAndFilterOptionsContainer,
  Divider,
  QuestionsAndAnswerContainer,
  SkeletonContainer,
  HcpInformationContainer,
  TableContainer,
  PromsGraphContainer,
  ScoreContainer,
  ImageText,
  previewImageModalCss,
  ImagePreviewContainer,
} from "./PromDetailsModal.style";
import {
  promDetailsTableColumns,
  promQuestionsAndAnswersTableColumns,
} from "./promDetailsTableColumns";
import { ActionContainer } from "../../../../../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import {
  Dropdown,
  DropdownTextItem,
} from "../../../../../../components/Dropdown";
import { CustomIconButton } from "../../../../../../components/CustomIconButton";
import { Dots } from "../../../../../../assets/icons/Dots";
import { ArchiveAnswerModal } from "./ArchiveAnswerModal/ArchiveAnswerModal";
import { promsService } from "../../../../../../services/promsService";
import { useToast } from "../../../../../../hooks/useToast";
import { queryClient } from "../../../../../../config/query";
import { Modal } from "antd";
import { AnswerClinicianFormModal } from "../../../AddFormModal/AnswerClinicianFormModal";
import { usePatientInfo } from "../../../../../../hooks/queries/patients";
import { getSurveyFormsAnswersToEdit } from "../../../../../../utils/surveysUtils";
import {
  getFileNameFromPath,
  isVideoPath,
  parsePaths,
} from "../../../../../../utils/surveyMediaAnswer";
import { ISurveyAnswer } from "../../../../../../@types/Surveys";
import { ButtonContainer } from "./ArchiveAnswerModal/ArchiveAnswerModal.styles";
import { Button } from "../../../../../../components/Button";

type PromDetailsModalProps = {
  patientId: number;
  formData: IPromsTableData;
};

export const FormTableActions = {
  Edit: "Edit",
  Delete: "Delete",
};

type FormTableAction = (typeof FormTableActions)[keyof typeof FormTableActions];

export function PromDetailsModal({
  patientId,
  formData,
}: PromDetailsModalProps) {
  const { t } = useTranslation();

  const { portalPreferences, changePortalPreferences, getDateRangeFilter } =
    useUserPreferences();
  const [selectedFilterDate, setSelectedFilterDate] =
    useState<DateFilterOptionType>(getDateRangeFilter);

  const { surveyForms, maxScores, isFetching, refetch } =
    getPatientSurveyFormsData(
      patientId,
      formData.id,
      selectedFilterDate.startDate.toISOString(),
      selectedFilterDate.endDate.toISOString()
    );

  const { patient } = usePatientInfo({ patientId: patientId.toString() });

  const [completedDate, setCompletedDate] = useState("");
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<
    ISurveyFormQuestionTableData[]
  >([]);
  const [domainScores, setDomainScores] = useState<ISurveyFormScore[]>([]);
  const [isTableView, setIsTableView] = useState<boolean>(
    surveyForms && surveyForms.length && surveyForms[0].displayRules
      ? surveyForms[0]?.displayRules?.type !== "TotalScoreGraphWithLegend"
      : true
  );
  const [hasGraphView, setHasGraphView] = useState<boolean>(
    surveyForms && surveyForms.length && surveyForms[0].displayRules
      ? surveyForms[0]?.displayRules?.type === "TotalScoreGraphWithLegend"
      : false
  );
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>();
  const [formattedDateRange, setFormattedDateRange] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const [surveyAnswerToArchive, setSurveyAnswerToArchive] = useState<{
    time: string;
    surveyAnswerId: string;
  }>();
  const [isArchivingLoading, setIsArchivingLoading] = useState<boolean>(false);

  const [surveyAnswerToEditId, setSurveyAnswerToEditId] = useState<string>();
  const [surveyAnswersToEdit, setSurveyAnswersToEdit] =
    useState<ISurveyAnswer[]>();

  const [previewMedia, setPreviewMedia] = useState<
    { path: string; preSignedUrl: string } | undefined
  >();

  const { showToast } = useToast();

  let newTableColums: ColumnDef<any, string>[] = [];

  const surveyType = formData.type;
  const surveyId = formData.id;

  const roundScoreToOneDecimal = (score: number) => {
    return Math.round(score * 10) / 10;
  };

  // Max possible score per column, keyed by "Total" and by domain name. Only populated for
  // surveys with a score strategy, so unscored surveys keep their plain headers.
  const maxScoreByKey = useMemo(() => {
    const map = new Map<string, number>();
    maxScores?.forEach((maxScore) => {
      const key = maxScore.type === "Total" ? "Total" : maxScore.domain ?? "";
      if (key) map.set(key, maxScore.maxScore);
    });
    return map;
  }, [maxScores]);

  const formatScoreHeader = (label: string, key: string) => {
    const max = maxScoreByKey.get(key);
    // Non-breaking space keeps "(max N)" together so a narrow column wraps it onto its own line
    // (as in the design) instead of splitting between "max" and the number.
    return max !== undefined
      ? `${label} (max${String.fromCharCode(160)}${roundScoreToOneDecimal(
          max
        )})`
      : label;
  };

  const handleRowClick = async (rowData: ISurveyPromsTableData) => {
    const dataToDisplay = await Promise.all(
      rowData.questions.map(async (data) => {
        let { answerText } = data;
        if (answerText && typeof answerText === "string") {
          if (data.type === "Date") {
            answerText = dayjs(answerText).format(LONG_DATE_TIME_FORMAT);
          }

          if (data.type === "Media") {
            const paths = parsePaths(answerText);
            const links = await Promise.all(
              paths.map(async (path) => ({
                path,
                preSignedUrl: (
                  await promsService.getMediaPreviewLink(path)
                ).data.preSignedUrl,
              }))
            );

            answerText = (
              <>
                {links.map(({ path, preSignedUrl }) => (
                  <ImageText
                    key={path}
                    onClick={() => setPreviewMedia({ path, preSignedUrl })}
                  >
                    {getFileNameFromPath(path) ||
                      (isVideoPath(path)
                        ? t("patientPage_proms_details_viewVideo")
                        : t("patientPage_proms_details_viewImage"))}
                  </ImageText>
                ))}
              </>
            );
          }
        }

        return {
          key: data.key,
          question: data.questionText,
          answer:
            answerText == null || answerText === ""
              ? t("patientPage_proms_noAnswer")
              : answerText,
          type: data.type,
        };
      })
    );

    setSelectedSurveyId(rowData.id.toString());
    setCompletedDate(rowData.time);
    setQuestionsAndAnswers(dataToDisplay);
    setDomainScores(rowData.scores ?? []);
  };

  const handleOnChangeDateRangeFilter = (
    dateRangeFilter: string,
    customFromDate?: dayjs.Dayjs,
    customToDate?: dayjs.Dayjs
  ) => {
    const newPortalPreferences = {
      ...portalPreferences,
      dateRangeFilter,
      customFromDate,
      customToDate,
    };
    changePortalPreferences(newPortalPreferences);

    if (dateRangeFilter !== "custom") {
      userService.updateDateRangeFilter(dateRangeFilter);
    }
  };

  const handleFormAnswerArchive = async (confirm: boolean) => {
    if (confirm) {
      setIsArchivingLoading(true);
      const request = await promsService.archiveFormAnswer(
        surveyAnswerToArchive?.surveyAnswerId!
      );

      const responseStatus = request.status;
      setIsArchivingLoading(false);

      if (responseStatus >= 200 && responseStatus < 300) {
        showToast(t("patientPage_proms_details_archived"), "success", {
          rightMargin: "50px",
        });

        queryClient.refetchQueries({
          queryKey: [`patient-survey-forms-${patientId}-${surveyId}`],
        });
        queryClient.refetchQueries({
          queryKey: ["patient-proms-infinite", patientId],
          exact: false,
        });
      } else {
        showToast(t("patientPage_proms_details_submitFailed"), "error", {
          rightMargin: "250px",
        });
      }
    }
    setSurveyAnswerToArchive(undefined);
  };

  const parsedSurveyForms = useMemo(() => {
    if (!surveyForms) return [];

    const domains = new Set<string>();

    // Domains come from the survey's max scores (authoritative for the current definition), so
    // the domain columns show even for answers with no stored scores (e.g. legacy answers). Fall
    // back to the answers' own domains to stay robust if a stored domain isn't in the max set.
    maxScoreByKey.forEach((_max, key) => {
      if (key !== "Total") {
        domains.add(key);
      }
    });

    surveyForms.forEach((surveyForm) => {
      if (surveyForm.scores) {
        surveyForm.scores.forEach((score) => {
          if (score.type === "Domain" && score.domain) {
            domains.add(score.domain);
          }
        });
      }
    });

    newTableColums = [
      createColumn({
        keyName: "total",
        header: () => formatScoreHeader(t("patientPage_proms_total"), "Total"),
        cell: (info) => info.renderValue(),
      }),
      ...promDetailsTableColumns.slice(1),
    ];

    let newColumns: ColumnDef<any, string>[] = [];

    domains.forEach((domain) =>
      newColumns.push(
        createColumn({
          keyName: domain,
          header: () => formatScoreHeader(domain, domain),
          cell: (info) => info.renderValue(),
        })
      )
    );

    if (
      surveyForms[0]?.displayRules?.domainAndTotalLegends &&
      surveyForms[0]?.displayRules?.type === "DomainAndTotalGraphWithLegend"
    ) {
      const { domainAndTotalLegends } = surveyForms[0].displayRules;

      newColumns = newColumns.sort((a, b) => {
        const keyA = (a as any).accessorKey;
        const keyB = (b as any).accessorKey;

        const indexA = domainAndTotalLegends.findIndex(
          (l) => l.domain === keyA
        );
        const indexB = domainAndTotalLegends.findIndex(
          (l) => l.domain === keyB
        );

        return indexA - indexB;
      });
    }

    newTableColums.splice(1, 0, ...newColumns);

    if (surveyType === "Clinician") {
      newTableColums.push({
        accessorKey: "action",
        header: () => "",
        cell: (info) => info.renderValue(),
        size: 30,
        enableSorting: false,
      });
    }

    const surveyFormList = surveyForms.map(
      (surveyForm: ISurveyPromsTableData) => {
        const domainScores: { [key: string]: string | number } = {};

        domains.forEach((domain) => {
          const domainScore = surveyForm.scores?.find(
            (score) => score.domain === domain
          )?.score;
          domainScores[domain] =
            domainScore !== undefined && domainScore >= 0
              ? roundScoreToOneDecimal(domainScore)
              : "-";
        });

        const totalScore = surveyForm.scores?.find(
          (x) => x.type === "Total"
        )?.score;

        return {
          id: surveyForm.id,
          time: `${getLongFormattedDate(surveyForm.time)}${
            surveyForm.lastUpdated ? ` ${t("patientPage_proms_edited")}` : ""
          }`,
          questions: surveyForm.questions.filter(
            (question) => question.type !== "Informative"
          ),
          surveyResult: surveyForm.surveyResult ?? "",
          total:
            totalScore !== undefined ? roundScoreToOneDecimal(totalScore) : "",
          ...domainScores,
          scores: surveyForm.scores,
          archived: surveyForm.archived,
          action: !surveyForm.archived ? (
            <div>
              <ActionContainer
                className="actionContainer"
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  trigger={
                    <CustomIconButton>
                      <Dots />
                    </CustomIconButton>
                  }
                  value=""
                  onValueChange={(val: FormTableAction) => {
                    if (val === FormTableActions.Delete) {
                      setSurveyAnswerToArchive({
                        surveyAnswerId: surveyForm.id.toString(),
                        time: surveyForm.time,
                      });
                    }
                    if (val === FormTableActions.Edit) {
                      setSurveyAnswerToEditId(surveyForm.id.toString());
                      setSurveyAnswersToEdit(
                        getSurveyFormsAnswersToEdit(surveyForm.questions)
                      );
                    }
                  }}
                >
                  <DropdownTextItem
                    key="Edit"
                    value="Edit"
                    text={t("patientPage_proms_details_edit")}
                  />
                  <DropdownTextItem
                    key="Delete"
                    value="Delete"
                    text={t("patientPage_proms_details_delete")}
                  />
                </Dropdown>
              </ActionContainer>
            </div>
          ) : null,
        };
      }
    );

    return surveyFormList;
  }, [surveyForms, maxScoreByKey, t]);

  const handleOnLineDotClick = async (surveyId: number) => {
    const surveyForm = parsedSurveyForms.find((sf) => sf.id === surveyId);

    if (!surveyForm) return;

    const dataToDisplay = await Promise.all(
      surveyForm.questions.map(async (data) => {
        let { answerText } = data;
        if (answerText && typeof answerText === "string") {
          if (data.type === "Date") {
            answerText = dayjs(answerText).format(LONG_DATE_TIME_FORMAT);
          }

          if (data.type === "Media") {
            const paths = parsePaths(answerText);
            const links = await Promise.all(
              paths.map(async (path) => ({
                path,
                preSignedUrl: (
                  await promsService.getMediaPreviewLink(path)
                ).data.preSignedUrl,
              }))
            );

            answerText = (
              <>
                {links.map(({ path, preSignedUrl }) => (
                  <ImageText
                    key={path}
                    onClick={() => setPreviewMedia({ path, preSignedUrl })}
                  >
                    {getFileNameFromPath(path) ||
                      (isVideoPath(path)
                        ? t("patientPage_proms_details_viewVideo")
                        : t("patientPage_proms_details_viewImage"))}
                  </ImageText>
                ))}
              </>
            );
          }
        }

        return {
          key: data.key,
          question: data.questionText,
          answer:
            answerText == null || answerText === ""
              ? t("patientPage_proms_noAnswer")
              : answerText,
          type: data.type,
        };
      })
    );

    setSelectedSurveyId(surveyId.toString());
    setCompletedDate(surveyForm.time);
    setQuestionsAndAnswers(dataToDisplay);
    setDomainScores(surveyForm.scores ?? []);
  };

  const handleScores = () => {
    const scores: { domain: string; score: number }[] = [];

    domainScores.forEach((score) => {
      if (score.type === "Total") {
        scores.unshift({
          // The label shown in the score summary line; the graph grouping key below stays
          // the API's "Total" (EN-5191).
          domain: t("patientPage_proms_total"),
          score: roundScoreToOneDecimal(score.score),
        });
      } else if (score.domain) {
        scores.push({
          domain: score.domain,
          score: roundScoreToOneDecimal(score.score),
        });
      }
    });

    return (
      <p>
        {scores.map(
          (score, index) =>
            `${score.domain}: ${score.score}${
              index !== scores.length - 1 ? ", " : ""
            }`
        )}
      </p>
    );
  };

  const graphData = useMemo(() => {
    const data: IPromsLineGraphData[] = [];
    surveyForms?.forEach((surveyForm) => {
      const totalScore = surveyForm.scores?.find(
        (x) => x.type === "Total"
      )?.score;

      if (totalScore !== undefined) {
        data.push({
          id: surveyForm.id,
          time: surveyForm.time,
          totalScore: roundScoreToOneDecimal(totalScore),
          archived: surveyForm.archived,
        });
      }
    });
    return data;
  }, [surveyForms]);

  const domainTotalGraphData = useMemo(() => {
    if (!surveyForms || surveyForms.length === 0) return [];

    const data: IPromsDomainTotalGraphData[] = [];

    surveyForms?.forEach((surveyForm) => {
      surveyForm.scores?.forEach((score) => {
        if (score.type === "Domain" && score.domain) {
          data.push({
            id: surveyForm.id,
            time: surveyForm.time,
            score: score.score,
            domain: score.domain,
            archived: surveyForm.archived,
          });
        }

        if (score.type === "Total") {
          data.push({
            id: surveyForm.id,
            time: surveyForm.time,
            score: score.score,
            domain: "Total",
            archived: surveyForm.archived,
          });
        }
      });
    });
    return data;
  }, [surveyForms]);

  const lineGraphMainValues = useMemo(() => {
    if (graphData.length === 0) return {};

    const minData = graphData.reduce(
      (minObj, current) =>
        current.totalScore < minObj.totalScore ? current : minObj,
      graphData[0]
    );

    const maxData = graphData.reduce(
      (maxObj, current) =>
        current.totalScore > maxObj.totalScore ? current : maxObj,
      graphData[0]
    );
    const average = roundToX(
      graphData.reduce((sum, current) => sum + current.totalScore, 0) /
        graphData.length,
      2
    );

    const minDate = graphData.reduce((prev, current) =>
      prev.time < current.time ? prev : current
    ).time;
    const maxDate = graphData.reduce((prev, current) =>
      prev.time > current.time ? prev : current
    ).time;

    return {
      min: roundToX(minData.totalScore, 2),
      max: roundToX(maxData.totalScore, 2),
      minValueDate: minData.time ? dayjs(minData.time) : undefined,
      maxValueDate: maxData.time ? dayjs(maxData.time) : undefined,
      minDate: minDate ? dayjs(minDate) : undefined,
      maxDate: maxDate ? dayjs(maxDate) : undefined,
      average: roundToX(average, 2),
    };
  }, [graphData]);

  const scatterGraphData = useMemo(() => {
    if (!surveyForms || surveyForms.length === 0) return [];

    const { displayRules } = surveyForms[0];

    const data = surveyForms.flatMap((surveyForm) =>
      surveyForm.questions.map((question) => ({
        questionId: question.questionId,
        questionOptionIds: question.questionOptionIds,
        time: surveyForm.time,
        surveyId: surveyForm.id,
        archived: surveyForm.archived,
      }))
    );

    const mappedData = data.map(
      ({ questionId, questionOptionIds, time, surveyId }) => {
        if (
          !displayRules ||
          !displayRules.legends ||
          !displayRules.yAxisLabels ||
          !displayRules.dataSourceType
        )
          return null;

        if (displayRules.dataSourceType === "Answers") {
          const legend = displayRules.legends.find((l) =>
            l.legendIds.includes(questionId)
          );

          const yAxisLabel = displayRules.yAxisLabels.find((yAxis) =>
            yAxis.labelIds.some((id) => questionOptionIds?.includes(id))
          );

          if (!legend || !yAxisLabel) return null;

          return {
            y: yAxisLabel?.text,
            x: dayjs(time).valueOf(),
            value: legend?.text,
            surveyId,
          };
        }

        const legend = displayRules.legends.find((l) =>
          l.legendIds.some((id) => questionOptionIds?.includes(id))
        );

        const yAxisLabel = displayRules.yAxisLabels.find((yAxis) =>
          yAxis.labelIds.includes(questionId)
        ) as ISurveyFormDisplayRuleYAxisLabel;

        if (!legend || !yAxisLabel) return null;

        return {
          y: yAxisLabel?.text,
          x: dayjs(time).valueOf(),
          value: legend?.text,
          surveyId,
          archived: surveyForms.find((sf) => sf.id === surveyId)?.archived,
        };
      }
    );

    const filteredData = mappedData.filter(Boolean);

    return filteredData;
  }, [surveyForms]);

  const scatterGraphDateRange = useMemo(() => {
    if (scatterGraphData.length === 0) return {};

    const { min, max } = scatterGraphData.reduce(
      (acc, curr) => ({
        min: Math.min(acc.min, curr!.x),
        max: Math.max(acc.max, curr!.x),
      }),
      { min: scatterGraphData[0]!.x, max: scatterGraphData[0]!.x }
    );
    return {
      minDate: dayjs(min),
      maxDate: dayjs(max),
    };
  }, [scatterGraphData]);

  const renderContent = useMemo(() => {
    if (isFetching) {
      return (
        <SkeletonContainer>
          <SkeletonLoading height="100%" width="100%" />
        </SkeletonContainer>
      );
    }
    if (!surveyForms || surveyForms.length === 0) {
      return (
        <ImageContainer style={{ border: "0px", marginTop: "30px" }}>
          <PromsEmptyState width={181} height={158} />
          <NoContentMessage>
            {t("patientPage_proms_details_noData")}
          </NoContentMessage>
        </ImageContainer>
      );
    }

    const { displayRules } =
      surveyForms.length > 0 ? surveyForms[0] : { displayRules: undefined };

    if (isTableView || !displayRules) {
      return (
        <TableContainer>
          <Table
            data={parsedSurveyForms}
            columns={newTableColums}
            hasBorder={false}
            onRowClick={handleRowClick}
            rowPadding
            fontSize={14}
            selectedId={selectedSurveyId}
          />
        </TableContainer>
      );
    }

    if (graphData.length === 0) {
      return (
        <ImageContainer style={{ border: "0px", marginTop: "30px" }}>
          <PromsEmptyState width={181} height={158} />
          <NoContentMessage>
            {t("patientPage_proms_details_noData")}
          </NoContentMessage>
        </ImageContainer>
      );
    }

    if (displayRules.type === "TotalScoreGraphWithLegend") {
      const ranges =
        displayRules.ranges?.map((r) => {
          return {
            start: r.start,
            end: r.end,
            startComparator: r.startComparator ?? ">=",
            endComparator: r.startComparator !== "=" ? r.endComparator : "<",
            color: r.color,
            name: r.name,
          } as IPromsRange;
        }) ?? [];

      return (
        <PromsGraphContainer>
          <PromsLineGraph
            data={graphData}
            minY={displayRules.yAxisMin}
            maxY={displayRules.yAxisMax}
            mainValues={lineGraphMainValues}
            dateInterval={selectedFilterDate}
            showValues={questionsAndAnswers.length === 0}
            ranges={ranges}
            selectedId={selectedSurveyId}
            onDotClick={handleOnLineDotClick}
          />
        </PromsGraphContainer>
      );
    }

    if (displayRules.type === "AnswerPlotGraph") {
      const yAxisLabels = displayRules.yAxisLabels?.map((label) => label.text);

      const legendsColorMap = displayRules.legends
        ?.sort((a, b) => a.order - b.order)
        .reduce((acc, curr) => {
          acc[curr.text] = curr.color;
          return acc;
        }, {} as Record<string, string>);

      return (
        <PromsScatterGraph
          data={scatterGraphData}
          minDate={scatterGraphDateRange.minDate}
          maxDate={scatterGraphDateRange.maxDate}
          dateInterval={selectedFilterDate}
          yAxisLabels={yAxisLabels ?? []}
          legendsColorMap={legendsColorMap ?? {}}
          onDotClick={handleOnLineDotClick}
        />
      );
    }

    if (displayRules.type === "DomainAndTotalGraphWithLegend") {
      return (
        <PromsDomainTotalGraph
          data={domainTotalGraphData}
          minY={displayRules.yAxisMin}
          maxY={displayRules.yAxisMax}
          mainValues={lineGraphMainValues}
          dateInterval={selectedFilterDate}
          showValues={questionsAndAnswers.length === 0}
          legends={displayRules.domainAndTotalLegends ?? []}
          selectedId={selectedSurveyId}
          onDotClick={handleOnLineDotClick}
        />
      );
    }

    return null;
  }, [isFetching, graphData, lineGraphMainValues, t]);

  useEffect(() => {
    refetch();
    setQuestionsAndAnswers([]);
    setSelectedSurveyId(undefined);
  }, [selectedFilterDate]);

  useEffect(() => {
    if (isFetching) return;

    if (!surveyForms || !surveyForms.length || !surveyForms[0]?.displayRules) {
      setHasGraphView(false);
      setIsTableView(true);
      return;
    }

    const isGraph =
      surveyForms[0]?.displayRules?.type === "TotalScoreGraphWithLegend" ||
      surveyForms[0]?.displayRules?.type === "AnswerPlotGraph" ||
      surveyForms[0]?.displayRules?.type === "DomainAndTotalGraphWithLegend";

    setHasGraphView(isGraph);
    setIsTableView(!isGraph);
  }, [isFetching]);

  useEffect(() => {
    let minDate = dayjs();

    surveyForms?.forEach((surveyForm) => {
      if (dayjs(surveyForm.time).isBefore(minDate)) {
        minDate = dayjs(surveyForm.time);
      }
    });

    setFormattedDateRange(
      `${getShortFormattedDate(
        selectedFilterDate.id === DATE_FILTER_ALL
          ? minDate.format()
          : selectedFilterDate.startDate.format()
      )} - ${getShortFormattedDate(selectedFilterDate.endDate.format())}`
    );
  }, [surveyForms]);

  const handleFormCancel = () => {
    setSurveyAnswerToEditId(undefined);
  };

  const handleFormSubmit = (responseStatus?: number) => {
    setSurveyAnswerToEditId(undefined);

    if (responseStatus) {
      if (responseStatus >= 200 && responseStatus < 300) {
        showToast(t("patientPage_proms_details_edited"), "success", {
          rightMargin: "50px",
        });

        queryClient.refetchQueries({
          queryKey: ["patient-proms-infinite", patientId],
          exact: false,
        });
        refetch();

        setQuestionsAndAnswers([]);
        setSelectedSurveyId(undefined);
      } else {
        showToast(t("patientPage_proms_details_submitFailed"), "error", {
          rightMargin: "250px",
        });
      }
    }
  };

  const handleDownloadMedia = async (path: string) => {
    setIsDownloading(true);
    try {
      const result = await promsService.downloadMedia(path);

      const contentDisposition = result.headers["content-disposition"];

      const fileName =
        contentDisposition?.match(/filename\*=UTF-8''(.+)/)?.[1] ||
        contentDisposition?.match(/filename=(.+);?/)?.[1] ||
        `Patient-${patientId}-${dayjs().format("DD-MM-YYYY")}.jpg`;

      const url = URL.createObjectURL(new Blob([result.data]));
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: fileName,
      });

      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {!surveyAnswerToEditId ? (
        <PromDetailsModalContainer>
          {questionsAndAnswers.length > 0 && <Divider />}

          <FlexContainer>
            <HeaderContainer>
              <TitleAndFilterOptionsContainer>
                <Title>{formData.name}</Title>
                <Subtitle>{formattedDateRange}</Subtitle>

                {surveyForms?.[0]?.displayRules?.hcpInformation && (
                  <HcpInformationContainer>
                    {surveyForms[0].displayRules.hcpInformation}
                  </HcpInformationContainer>
                )}

                <PromsOptionsBar
                  cardTitle={formData.name}
                  selectedFilterDate={selectedFilterDate}
                  isTableView={isTableView}
                  hasGraphView={hasGraphView}
                  onChange={(selectedDateFilter) => {
                    const { id } = selectedDateFilter;

                    if (
                      id === DATE_FILTER_CUSTOM ||
                      id !== selectedFilterDate.id
                    ) {
                      setSelectedFilterDate(selectedDateFilter);
                      handleOnChangeDateRangeFilter(
                        id,
                        selectedDateFilter.startDate,
                        selectedDateFilter.endDate
                      );
                    }
                  }}
                  onTableViewChange={(value) => {
                    mixpanelActions.track(
                      value
                        ? "User Action: Survey table view"
                        : "User Action: Survey graph view"
                    );
                    setIsTableView(value);
                  }}
                />
              </TitleAndFilterOptionsContainer>

              <ContentContainer>{renderContent}</ContentContainer>
            </HeaderContainer>

            {questionsAndAnswers.length > 0 && (
              <QuestionsAndAnswerContainer>
                <Title selectedSurvey>
                  {t("patientPage_proms_details_selectedSurvey")}
                </Title>
                <Subtitle selectedSurvey>{completedDate}</Subtitle>

                {domainScores.length > 0 && (
                  <ScoreContainer>{handleScores()}</ScoreContainer>
                )}

                <TableContainer isQuestions>
                  <Table
                    data={questionsAndAnswers}
                    columns={promQuestionsAndAnswersTableColumns}
                    hasBorder={false}
                    rowPadding
                    rowPaddingRight="30px"
                    fontSize={14}
                  />
                </TableContainer>

                <Modal
                  open={previewMedia !== undefined}
                  zIndex={3001}
                  styles={{ mask: { pointerEvents: "all" } }}
                  width="max-content"
                  height="max-content"
                  centered
                  footer={null}
                  onCancel={() => setPreviewMedia(undefined)}
                  className={previewImageModalCss()}
                  destroyOnHidden
                >
                  <ImagePreviewContainer>
                    {previewMedia ? (
                      isVideoPath(previewMedia.path) ? (
                        <video
                          src={previewMedia.preSignedUrl}
                          controls
                          style={{ maxHeight: "700px", maxWidth: "1200px" }}
                        />
                      ) : (
                        <img
                          src={previewMedia.preSignedUrl}
                          alt={t("patientPage_proms_details_mediaAlt")}
                          style={{ maxHeight: "700px", maxWidth: "1200px" }}
                        />
                      )
                    ) : undefined}

                    <ButtonContainer>
                      <Button
                        isLoading={isDownloading}
                        label={
                          isDownloading
                            ? t("patientPage_proms_details_downloading")
                            : t("patientPage_proms_details_download")
                        }
                        type="button"
                        onClick={() =>
                          previewMedia
                            ? handleDownloadMedia(previewMedia.path)
                            : undefined
                        }
                      />
                    </ButtonContainer>
                  </ImagePreviewContainer>
                </Modal>
              </QuestionsAndAnswerContainer>
            )}
          </FlexContainer>

          {surveyAnswerToArchive && (
            <ArchiveAnswerModal
              isLoading={isArchivingLoading}
              time={surveyAnswerToArchive.time}
              handleConfirmModalClose={handleFormAnswerArchive}
            />
          )}
        </PromDetailsModalContainer>
      ) : (
        <AnswerClinicianFormModal
          patient={patient}
          surveyId={surveyId.toString()}
          surveyAnswerId={surveyAnswerToEditId}
          handleFormCancel={handleFormCancel}
          handleFormSubmit={handleFormSubmit}
          surveyAnswersToEdit={surveyAnswersToEdit}
        />
      )}
    </>
  );
}
